import React, { useEffect, useState } from 'react';
import Layout from './../../components/Layout';
import moment from "moment-timezone";
import { Button, Modal, Form, Row, Col, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrashAlt, faPlus } from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../../constants';
import { 
  BannerHeading, 
  CoursesWrapper, 
  BannerContainer, 
  CourseCard, 
  CourseTitle, 
  CourseDates, 
  CourseDescription 
} from "../../components/services/ServicesElements";
import axios from 'axios';
import ConfirmDeleteModal from '../../components/DeleteModal';
const CoursesScreen = () => {
  const [courses, setCourses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false); 
  const [loading, setLoading] = useState(false); 
  const [currentCourseId, setCurrentCourseId] = useState(null); 
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newCourse, setNewCourse] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    instructor: '',
    photo: null
  });
  const token = localStorage.getItem('token');

  // Fetch courses from the API
  useEffect(() => {
    const fetchCourses = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${BASE_URL}/course`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setCourses(data);
      } catch (error) {
        console.error('Error fetching courses:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, [token]);

  const handleSubmitCourse = async (e) => {
    e.preventDefault();
  
    // Validate if required fields are present
    if (!newCourse.name || !newCourse.description || !newCourse.startDate || !newCourse.endDate || !newCourse.instructor) {
      alert("Please fill in all the required fields.");
      return;
    }
  
    const formData = new FormData();
    formData.append('name', newCourse.name);
    formData.append('description', newCourse.description);
    formData.append('startDate', newCourse.startDate);
    formData.append('endDate', newCourse.endDate);
    formData.append('instructor', newCourse.instructor);
  
    // Add photo if it exists
    if (newCourse.photo) {
      formData.append('photo', newCourse.photo);
    }
  
    try {
      const response = isEditMode
        ? await axios.put(`${BASE_URL}/course/${currentCourseId}`, formData, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          })
        : await axios.post(`${BASE_URL}/course`, formData, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          });
  
      if (response.status === 201 || response.status === 200) {
        const updatedCourses = isEditMode
          ? courses.map(course => course._id === currentCourseId ? response.data : course)
          : [...courses, response.data];
  
        setCourses(updatedCourses);
        setShowModal(false); // Close the modal after submission
        setNewCourse({
          name: '',
          description: '',
          startDate: '',
          endDate: '',
          instructor: '',
          photo: null,
        }); 
        setIsEditMode(false); 
      }
    } catch (error) {
      console.error('Error submitting course:', error);
    }
  };

  const handleEdit = (course) => {
    setIsEditMode(true);
    setCurrentCourseId(course._id);  // Set the ID for the course being edited
    setNewCourse({
      name: course.name,
      description: course.description,
      startDate: moment(course.startDate).format('YYYY-MM-DD'),
      endDate: moment(course.endDate).format('YYYY-MM-DD'),
      instructor: course.instructor,
      photo: null,  // Reset the photo in case of an edit
    });
    setShowModal(true);
  };

  const handleDeleteCourse = async () => {
    try {
      const response = await axios.delete(`${BASE_URL}/course/${currentCourseId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setCourses(courses.filter(course => course._id !== currentCourseId)); // Remove the deleted course from the UI
      setShowDeleteModal(false); // Close delete modal after deletion
    } catch (error) {
      console.error('Error deleting course:', error);
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (courseId) => {
    setCurrentCourseId(courseId);
    setShowDeleteModal(true);
  };
  // Handle form input changes
  const handleChange = (e) => {
    if (e.target.name === 'photo') {
      setNewCourse({ ...newCourse, photo: e.target.files[0] });
    } else {
      setNewCourse({ ...newCourse, [e.target.name]: e.target.value });
    }
  };

  return (
    <Layout>
      <BannerContainer>
        <BannerHeading>Learning Opportunities</BannerHeading>
        <div className="d-flex justify-content-between align-items-center mb-3">
        <Button onClick={() => setShowModal(true)} variant="cyan">
          <FontAwesomeIcon icon={faPlus} className="me-2" />
                  <span className="small">Add New Course</span> 
        </Button> 
        </div>
        {loading ? (
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
        </div>
      ) :
      courses.length > 0 ? (     
        <Row xs={1} sm={2} md={3} lg={4} xl={4} className="g-4">
          {courses.map((course) => (
            <Col key={course._id}>
              <CourseCard className="course-card p-3 border rounded-3 d-flex flex-column h-100">
                {/* Displaying course photo if it exists */}
                {course.photo && (
                  <img 
                    src={course.photo} 
                    alt="Course" 
                    className="img-fluid rounded-3 mb-3" 
                    style={{ height: '200px', objectFit: 'cover' }}  // Fixed height with cover to maintain aspect ratio
                  />
                )}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span>{course.name}</span>
                  <div>
                    <FontAwesomeIcon 
                      icon={faEdit} 
                      onClick={() => handleEdit(course)} 
                      className="ms-2 text-primary cursor-pointer" 
                    />
                    <FontAwesomeIcon 
                        icon={faTrashAlt} 
                        onClick={() => openDeleteModal(course._id)} 
                        className="ms-2 text-danger cursor-pointer" 
                      />
                  </div>
                </div>
                <p className="text-muted flex-grow-1">{course.description}</p>
                <div className="text-muted">
                  <p><span>Start Date:</span> {moment(course.startDate).format('MMMM Do, YYYY')}</p>
                  <p><span>End Date:</span> {moment(course.endDate).format('MMMM Do, YYYY')}</p>
                </div>
              </CourseCard>
            </Col>
          ))}
        </Row>
      ) : (
    <div className="card mt-3">
  <div className="card-body">
    <p className="card-text">No Courses available. Please add courses to proceed.</p>
  </div>
</div>
  )}
      </BannerContainer>
  
      {/* Modal for Adding or Editing a Course */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditMode ? 'Edit Course' : 'Add New Course'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmitCourse}>
            <Form.Group>
              <Form.Label>Course Title</Form.Label>
              <Form.Control 
                type="text" 
                name="name"
                value={newCourse.name}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Description</Form.Label>
              <Form.Control 
                as="textarea" 
                name="description"
                value={newCourse.description}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Start Date</Form.Label>
              <Form.Control 
                type="date" 
                name="startDate"
                value={newCourse.startDate}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>End Date</Form.Label>
              <Form.Control 
                type="date" 
                name="endDate"
                value={newCourse.endDate}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Instructor</Form.Label>
              <Form.Control 
                type="text" 
                name="instructor"
                value={newCourse.instructor}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Course Photo</Form.Label>
              <Form.Control 
                type="file" 
                name="photo"
                onChange={handleChange}
              />
            </Form.Group>
            <div className="text-end mt-3">
  <Button variant="primary" type="submit">
    {isEditMode ? 'Update' : 'Save'}
  </Button>
</div>
          </Form>
        </Modal.Body>
      </Modal>
      <ConfirmDeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteCourse}
        itemName="this course"
      />
    </Layout>
  );
};

export default CoursesScreen;
